/**
 * Judge Training Clip 2 — Scoring Riders
 * Full browser, no zoom. AutoCursor targets by ID.
 * 390 frames at 30fps = 13 seconds.
 *
 * Matches the CURRENT Judge page layout:
 *   - 3-column grid: Rider (160px) | Attempts/Score (1fr) | Status (40px)
 *   - Multi-attempt: attempt BOXES overlay the score area by default
 *   - Click attempt box → score picker opens (with ← back)
 *   - Decimal sub-grid: click 8 → pick 8.0-8.9
 *   - Yellow border + dot = needs score, green = live attempt
 *   - After scoring, returns to attempt boxes showing the score
 *   - No CLR button
 *
 * Flow:
 *   1. Show attempt boxes with yellow "needs score" indicator
 *   2. Click an attempt box → score picker opens
 *   3. Click whole number 8 → decimal sub-grid
 *   4. Click 8.7 → score set, returns to attempt view
 *   5. Show second rider, click different attempt, score it
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { AutoCursor } from "../components/AutoCursor";
import { ScreenMock } from "../components/ScreenMock";
import { FadeTransition } from "../components/FadeTransition";
import { StepLabel } from "../components/StepLabel";

export function JudgeCourse2() {
  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0b", fontFamily: "'Inter', sans-serif" }}>
      <Sequence durationInFrames={55}><Title /></Sequence>
      <Sequence from={45} durationInFrames={305}>
        <FadeTransition durationInFrames={305} fadeIn={10} fadeOut={10}><Demo /></FadeTransition>
      </Sequence>
      <Sequence from={340} durationInFrames={50}><Done /></Sequence>
    </AbsoluteFill>
  );
}

function Title() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12, 40, 55], [0, 1, 1, 0], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ fontSize: 10, color: "#3b82f6", textTransform: "uppercase", letterSpacing: 3, fontFamily: "'JetBrains Mono', monospace", marginBottom: 14 }}>Judge Training · Clip 2</div>
      <div style={{ fontSize: 52, fontWeight: 800, color: "#fff" }}>Scoring Riders</div>
    </AbsoluteFill>
  );
}

/*
 * Timeline (frames relative to Demo sequence):
 *   0-40:   See attempt boxes (Dallas has yellow "needs score" on attempt 2)
 *   40:     Click Dallas attempt 2 box → opens score picker
 *   70:     Click whole number "8" → decimal sub-grid opens
 *   100:    Click "8.7" → score set, returns to attempt boxes showing 8.7
 *   140:    Click Amber attempt 1 box (already scored, re-editing)
 *   170:    Click whole number "9" → decimal sub-grid
 *   195:    Click "9.3" → score set, returns to attempt boxes
 */

function Demo() {
  const frame = useCurrentFrame();

  // Dallas (row 2) states — spaced out for readability
  const dallasPhase =
    frame < 45 ? "attempts" :        // show attempt boxes
    frame < 85 ? "score-picker" :    // whole number grid
    frame < 120 ? "decimal" :        // decimal sub-grid
    "attempts-scored";               // back to attempts, score shown
  const dallasScore: number | null = frame >= 120 ? 8.7 : null;

  // Amber (row 3) states
  const amberPhase =
    frame < 170 ? "attempts" :       // show attempt boxes
    frame < 205 ? "score-picker" :   // whole number grid (re-edit)
    frame < 240 ? "decimal" :        // decimal sub-grid
    "attempts-scored";               // back to attempts, score changed
  const amberAttempt1Score: number | null = frame >= 240 ? 9.3 : 8.1;

  return (
    <AbsoluteFill>
      <ScreenMock url="raley.koda-software.com/competition/boardstock/judge">
        <div style={shell}>
          <div style={topbar}>
            <span style={brand}>RALEY</span>
            <span style={{ marginLeft: "auto", ...mono, fontSize: 11, color: "#71717a" }}>judge1@koda-software.com</span>
          </div>
          <div style={{ padding: "16px 20px" }}>
            {/* Header */}
            <div style={{ borderBottom: "1px solid #2a2a32", paddingBottom: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Judge: Sarah</div>
              <div style={{ fontSize: 11, color: "#71717a", marginTop: 2 }}>Boardstock Invitational · average scoring</div>
            </div>

            {/* Live heat banner */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", marginBottom: 12, background: "#111113", border: "1px solid #2a2a32" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", boxShadow: "0 0 6px #ef4444", flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: "#e4e4e7" }}>Live Heat: <span style={{ color: "#3b82f6" }}>Men's Open — Round 1</span></span>
            </div>

            {/* Division + heat tabs */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, fontWeight: 600, color: "#52525b", ...mono }}>MEN'S OPEN</div>
              <div style={{ display: "flex", gap: 6 }}>
                {["Round 1", "Semi-Finals", "Finals"].map((h, i) => (
                  <div key={h} style={{ padding: "3px 10px", fontSize: 10, fontWeight: 600, textTransform: "uppercase", background: i === 0 ? "#3b82f6" : "#1a1a1e", color: i === 0 ? "#fff" : "#71717a", border: `1px solid ${i === 0 ? "#3b82f6" : "#2a2a32"}` }}>{h}</div>
                ))}
              </div>
            </div>

            {/* Column header — matches real page */}
            <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 40px", gap: 8, padding: "0 8px", marginBottom: 4, alignItems: "center" }}>
              <span style={colLabel}>Rider</span>
              <span style={colLabel}>Attempts / Score</span>
              <span />
            </div>

            {/* Rider rows */}
            <RiderRow name="Harley Clifford" nick="The King" attempts={[8.5, 7.9, 9.1]} maxAttempts={3} currentAttempt={3} phase="attempts" prefix="harley" />
            <RiderRow name="Nic Rapa" nick="Raptor" attempts={[7.2, 6.8, null]} maxAttempts={3} currentAttempt={3} phase="attempts" prefix="nic" />
            <DallasRow phase={dallasPhase} score={dallasScore} />
            <AmberRow phase={amberPhase} attempt1Score={amberAttempt1Score} />
            <RiderRow name="Meagan Ethell" nick="Mega" attempts={[8.0, null, null]} maxAttempts={3} currentAttempt={2} phase="attempts" prefix="meagan" />
            <RiderRow name="Cory Teunissen" nick="CT" attempts={[null, null, null]} maxAttempts={3} currentAttempt={1} phase="attempts" prefix="cory" />
          </div>
        </div>

        <AutoCursor points={[
          { x: 500, y: 400, frame: 0 },
          // Step 1: Click Dallas attempt 2 box (needs score - yellow)
          { target: "dallas-att2", frame: 20 },
          { target: "dallas-att2", frame: 45, click: true },
          // Step 2: Pause, then click whole number 8
          { target: "dallas-w8", frame: 65 },
          { target: "dallas-w8", frame: 85, click: true },
          // Step 3: Pause, then click decimal 8.7
          { target: "dallas-d87", frame: 102 },
          { target: "dallas-d87", frame: 120, click: true },
          // Pause to show result
          { x: 500, y: 400, frame: 150 },
          // Step 4: Click Amber attempt 1 box (re-edit scored attempt)
          { target: "amber-att1", frame: 155 },
          { target: "amber-att1", frame: 170, click: true },
          // Step 5: Pause, then click whole number 9
          { target: "amber-w9", frame: 188 },
          { target: "amber-w9", frame: 205, click: true },
          // Step 6: Pause, then click decimal 9.3
          { target: "amber-d93", frame: 222 },
          { target: "amber-d93", frame: 240, click: true },
          // Hold to show final result
          { x: 500, y: 400, frame: 270 },
          { x: 500, y: 400, frame: 295 },
        ]} />
      </ScreenMock>

      <StepLabel text="Yellow boxes need scoring — click to open the score picker" show={5} hide={50} color="#eab308" />
      <StepLabel text="Pick a whole number, then choose the exact decimal" show={50} hide={125} color="#3b82f6" />
      <StepLabel text="Score submitted — attempt box now shows 8.7" show={125} hide={165} color="#22c55e" />
      <StepLabel text="Click any scored attempt to change your score" show={165} hide={245} color="#a855f7" />
      <StepLabel text="Each attempt is scored independently" show={250} hide={290} color="#22c55e" />
    </AbsoluteFill>
  );
}

function Done() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 24, color: "#fff" }}>&#10003;</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }}>Use the full 0-10 range for consistent scoring</div>
    </AbsoluteFill>
  );
}

/* ── Static rider row (attempt boxes view, no interaction) ── */

function RiderRow({ name, nick, attempts, maxAttempts, currentAttempt, phase: _phase, prefix: _prefix }: {
  name: string; nick: string; attempts: (number | null)[]; maxAttempts: number; currentAttempt: number; phase: string; prefix: string;
}) {
  const hasAnyScore = attempts.some((a) => a !== null);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 40px", gap: 8, padding: "6px 8px", alignItems: "center", background: hasAnyScore ? "#111113" : "transparent", border: "1px solid #2a2a32", marginBottom: 2 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13 }}>{name}</div>
        <div style={{ fontSize: 10, color: "#52525b" }}>{nick}</div>
      </div>
      <div style={{ display: "flex", gap: 2 }}>
        {Array.from({ length: maxAttempts }, (_, i) => {
          const score = attempts[i] ?? null;
          const hasScore = score !== null;
          const isAvailable = i + 1 <= currentAttempt;
          const isFuture = !isAvailable;
          const needsScore = isAvailable && !hasScore;
          const isLive = i + 1 === currentAttempt;
          return (
            <div key={i} style={{
              flex: "1 1 0", minWidth: 0, height: 32, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
              fontSize: hasScore ? 11 : 9, fontWeight: hasScore ? 700 : 500, ...mono,
              background: hasScore ? "#1a1a1e" : needsScore ? "rgba(234,179,8,0.1)" : "transparent",
              color: hasScore ? "#e4e4e7" : needsScore ? "#eab308" : isFuture ? "#2a2a32" : "#52525b",
              border: `1px solid ${needsScore ? "#eab308" : isLive ? "#22c55e" : "#2a2a32"}`,
              opacity: isFuture ? 0.3 : 1,
            }}>
              {hasScore ? score.toFixed(1) : `#${i + 1}`}
              {needsScore && <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#eab308" }} />}
              {isLive && hasScore && <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e" }} />}
            </div>
          );
        })}
      </div>
      <div />
    </div>
  );
}

/* ── Dallas row — interactive: attempts → score picker → decimal → scored ── */

function DallasRow({ phase, score }: { phase: string; score: number | null }) {
  const showAttempts = phase === "attempts" || phase === "attempts-scored";
  const showScorePicker = phase === "score-picker";
  const showDecimal = phase === "decimal";

  return (
    <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 40px", gap: 8, padding: "6px 8px", alignItems: "center", background: score != null ? "#111113" : "transparent", border: "1px solid #2a2a32", marginBottom: 2 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13 }}>Dallas Friday</div>
        <div style={{ fontSize: 10, color: "#52525b" }}>D-Fri</div>
      </div>
      <div style={{ display: "flex", gap: 2 }}>
        {showAttempts && (
          <>
            {/* Attempt 1: scored 7.4 */}
            <div style={{ ...attemptBox, background: "#1a1a1e", color: "#e4e4e7", border: "1px solid #2a2a32", fontWeight: 700, fontSize: 11 }}>7.4</div>
            {/* Attempt 2: needs score OR just scored */}
            <div id="dallas-att2" style={{
              ...attemptBox,
              background: score != null ? "#1a1a1e" : "rgba(234,179,8,0.1)",
              color: score != null ? "#e4e4e7" : "#eab308",
              border: `1px solid ${score != null ? "#22c55e" : "#eab308"}`,
              fontWeight: score != null ? 700 : 500,
              fontSize: score != null ? 11 : 9,
              boxShadow: score != null ? "0 0 0 1px #22c55e" : "none",
            }}>
              {score != null ? score.toFixed(1) : "#2"}
              {score == null && <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#eab308" }} />}
              {score != null && <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e" }} />}
            </div>
            {/* Attempt 3: future */}
            <div style={{ ...attemptBox, opacity: 0.3, color: "#2a2a32", border: "1px solid #2a2a32", fontSize: 9 }}>#3</div>
          </>
        )}
        {showScorePicker && (
          <>
            <div style={{ ...backBtn }}>&#8592;</div>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((v) => (
              <div key={v} id={`dallas-w${v}`} style={{ ...scoreBtn, background: "#1a1a1e", color: "#52525b" }}>{v}</div>
            ))}
          </>
        )}
        {showDecimal && (
          <div style={{ display: "flex", gap: 2, width: "100%" }}>
            <div style={{ ...backBtn }}>&#8592;</div>
            {Array.from({ length: 10 }, (_, i) => {
              const val = 8 + i * 0.1;
              const rounded = Math.round(val * 10) / 10;
              return (
                <div key={rounded} id={`dallas-d${rounded.toFixed(1).replace(".", "")}`} style={{ ...scoreBtn, background: "#1a1a1e", color: "#52525b", fontSize: 8 }}>
                  {rounded.toFixed(1)}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ fontSize: 9, color: "#52525b", textAlign: "center" }}>{score == null && showAttempts ? "" : ""}</div>
    </div>
  );
}

/* ── Amber row — re-edit a scored attempt ── */

function AmberRow({ phase, attempt1Score }: { phase: string; attempt1Score: number | null }) {
  const showAttempts = phase === "attempts" || phase === "attempts-scored";
  const showScorePicker = phase === "score-picker";
  const showDecimal = phase === "decimal";

  return (
    <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 40px", gap: 8, padding: "6px 8px", alignItems: "center", background: "#111113", border: "1px solid #2a2a32", marginBottom: 2 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13 }}>Amber Wing</div>
        <div style={{ fontSize: 10, color: "#52525b" }}>Wings</div>
      </div>
      <div style={{ display: "flex", gap: 2 }}>
        {showAttempts && (
          <>
            <div id="amber-att1" style={{ ...attemptBox, background: "#1a1a1e", color: "#e4e4e7", border: "1px solid #2a2a32", fontWeight: 700, fontSize: 11 }}>
              {(attempt1Score ?? 8.1).toFixed(1)}
            </div>
            <div style={{ ...attemptBox, background: "#1a1a1e", color: "#e4e4e7", border: "1px solid #2a2a32", fontWeight: 700, fontSize: 11 }}>8.9</div>
            <div style={{ ...attemptBox, background: "#1a1a1e", color: "#e4e4e7", border: "1px solid #22c55e", fontWeight: 700, fontSize: 11, boxShadow: "0 0 0 1px #22c55e" }}>
              9.0
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e" }} />
            </div>
          </>
        )}
        {showScorePicker && (
          <>
            <div style={{ ...backBtn }}>&#8592;</div>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((v) => {
              const isSelected = v === 8 && attempt1Score === 8.1;
              return (
                <div key={v} id={`amber-w${v}`} style={{ ...scoreBtn, background: isSelected ? "#3b82f6" : "#1a1a1e", color: isSelected ? "#fff" : "#52525b", fontWeight: isSelected ? 700 : 500 }}>
                  {isSelected ? "8.1" : v}
                </div>
              );
            })}
          </>
        )}
        {showDecimal && (
          <div style={{ display: "flex", gap: 2, width: "100%" }}>
            <div style={{ ...backBtn }}>&#8592;</div>
            {Array.from({ length: 10 }, (_, i) => {
              const val = 9 + i * 0.1;
              const rounded = Math.round(val * 10) / 10;
              return (
                <div key={rounded} id={`amber-d${rounded.toFixed(1).replace(".", "")}`} style={{ ...scoreBtn, background: "#1a1a1e", color: "#52525b", fontSize: 8 }}>
                  {rounded.toFixed(1)}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div />
    </div>
  );
}

/* ── Shared styles ── */

const attemptBox: React.CSSProperties = {
  flex: "1 1 0", minWidth: 0, height: 32, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
  fontFamily: "'JetBrains Mono', monospace", cursor: "pointer", padding: 0,
};

const scoreBtn: React.CSSProperties = {
  flex: "1 1 0", minWidth: 0, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: 11, fontFamily: "'JetBrains Mono', monospace", cursor: "pointer",
};

const backBtn: React.CSSProperties = {
  flex: "0 0 24px", height: 32, display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: 10, fontWeight: 600, background: "#1a1a1e", color: "#71717a", border: "1px solid #2a2a32", cursor: "pointer",
};

const shell: React.CSSProperties = { width: "100%", height: "100%", background: "#0a0a0b", fontFamily: "'Inter', sans-serif", color: "#e4e4e7", display: "flex", flexDirection: "column" };
const topbar: React.CSSProperties = { height: 40, background: "#111113", borderBottom: "1px solid #2a2a32", display: "flex", alignItems: "center", gap: 24, padding: "0 20px", flexShrink: 0 };
const brand: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, fontSize: 14, letterSpacing: 2 };
const mono: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" };
const colLabel: React.CSSProperties = { fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#52525b", fontFamily: "'JetBrains Mono', monospace" };
